import { inject, Injectable, signal } from '@angular/core';

import { TranslocoService } from '@jsverse/transloco';

import { AiInsight } from '../../../../../../providers/semantria-api/ai-insights.service';
import {
  SemantriaUser,
  SemantriaUsersService,
} from '../../../../../../providers/semantria-api/semantria-users.service';
import { AlertService } from '../../../../../../services/alert.service';
import { InsightImageLoaderService } from '../../../../services/insight-image-loader.service';

@Injectable()
export class InsightCardFacade {
  isImageLoading = signal(false);
  insightImage = signal<string | ArrayBuffer | undefined>(undefined);
  imageExists = signal(false);

  publisherDisplayName = signal<string>('');

  private readonly userDisplayNameCache = new Map<string, string>();

  private readonly alertService = inject(AlertService);
  private readonly transloco = inject(TranslocoService);
  private readonly insightImageLoader = inject(InsightImageLoaderService);
  private readonly semantriaUsersService = inject(SemantriaUsersService);

  onInsightChanged(insight: AiInsight | undefined): void {
    if (!insight) {
      this.imageExists.set(false);
      this.publisherDisplayName.set('');
      return;
    }

    this.imageExists.set((insight.images?.length ?? 0) > 0);
    this.loadPublisherDisplayName(insight);
  }

  async ensureInsightImageLoaded(insight: AiInsight | undefined): Promise<void> {
    if (!insight || this.insightImage() || this.isImageLoading()) {
      return;
    }

    this.isImageLoading.set(true);
    try {
      const image = await this.insightImageLoader.load(
        insight.insight_id,
        insight.images?.[0]?.image_id
      );

      this.insightImage.set(image);
      this.imageExists.set(!!image);
    } catch {
      this.alertService.showErrorToast(
        this.transloco.translate('widgets.publish-insight-dialog.error-loading-image')
      );
    } finally {
      this.isImageLoading.set(false);
    }
  }

  private loadPublisherDisplayName(insight: AiInsight): void {
    const publisherId = insight.publisher_user_id;
    if (!publisherId) {
      this.publisherDisplayName.set('');
      return;
    }
    // prevent repeated getUser(publisherId) calls and improve performance/UX.
    const cached = this.userDisplayNameCache.get(publisherId);
    if (cached) {
      this.publisherDisplayName.set(cached);
      return;
    }

    this.semantriaUsersService
      .getUser(publisherId)
      .then((user: SemantriaUser) => {
        const name = `${user.first_name ?? ''} ${user.last_name ?? ''}`.trim();
        const resolved = name;
        this.userDisplayNameCache.set(publisherId, resolved);
        this.publisherDisplayName.set(resolved);
      })
      .catch(() => {
        this.publisherDisplayName.set(publisherId);
      });
  }
}
